import { useLayoutEffect, useRef, useState, useCallback } from "react";
import mermaid from "mermaid";
import { Button } from "@/shared/components/ui/button";
import { Download, ZoomIn, ZoomOut, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "@/shared/hooks/use-toast";
import { useMermaidPalette } from "@/shared/hooks/useMermaidPalette";

function initMermaid(themeVariables: Record<string, string>) {
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: "loose",
    theme: "base",
    themeVariables: { ...themeVariables, fontFamily: "inherit" },
    flowchart: { curve: "basis", padding: 16 },
  });
}

function extractMermaidCode(raw: string): string {
  const trimmed = raw.trim();
  const fenceMatch = trimmed.match(/^```(?:mermaid)?\s*([\s\S]*?)```$/m);
  if (fenceMatch) return fenceMatch[1].trim();
  if (trimmed.startsWith("graph ") || trimmed.startsWith("flowchart ") || trimmed.startsWith("sequenceDiagram") || trimmed.startsWith("stateDiagram")) {
    return trimmed;
  }
  return trimmed;
}

interface MermaidRendererProps {
  code: string;
  className?: string;
  onError?: (message: string) => void;
  onRetry?: () => void;
}

export function MermaidRenderer({ code, className = "", onError, onRetry }: MermaidRendererProps) {
  const { palette } = useMermaidPalette();
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    if (!code?.trim()) {
      setSvg(null);
      setError(null);
      return;
    }

    const mermaidCode = extractMermaidCode(code);
    if (!mermaidCode) {
      setSvg(null);
      setError("Código Mermaid vazio ou inválido.");
      onError?.("Código Mermaid vazio ou inválido.");
      return;
    }

    initMermaid(palette.themeVariables);
    const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    mermaid
      .parse(mermaidCode)
      .then(() =>
        mermaid.render(id, mermaidCode)
      )
      .then(({ svg: renderedSvg }) => {
        setSvg(renderedSvg);
        setError(null);
      })
      .catch((err: Error) => {
        const msg = "A IA está refinando o diagrama, tente novamente.";
        setError(msg);
        setSvg(null);
        onError?.(msg);
        console.warn("[MermaidRenderer] Parse/render error:", err);
      });
  }, [code, palette, onError]);

  const handleDownloadPng = useCallback(() => {
    if (!containerRef.current || !svg) return;
    try {
      const svgEl = containerRef.current.querySelector("svg");
      if (!svgEl) {
        toast({ title: "Erro", description: "Diagrama não encontrado para exportar.", variant: "destructive" });
        return;
      }
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        toast({ title: "Erro", description: "Não foi possível criar o canvas.", variant: "destructive" });
        return;
      }
      const img = new Image();
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      img.onload = () => {
        const scaleFactor = 2;
        canvas.width = img.width * scaleFactor;
        canvas.height = img.height * scaleFactor;
        ctx.scale(scaleFactor, scaleFactor);
        ctx.fillStyle = palette.themeVariables.background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `fluxograma-caso-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(a.href);
          toast({ title: "Download concluído", description: "O diagrama foi salvo como PNG." });
        }, "image/png");
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        toast({ title: "Erro na exportação", description: "Não foi possível converter o diagrama.", variant: "destructive" });
      };
      img.src = url;
    } catch (e) {
      toast({ title: "Erro na exportação", description: (e as Error).message, variant: "destructive" });
    }
  }, [svg, palette.themeVariables.background]);

  if (error) {
    return (
      <div className={`rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center space-y-3 ${className}`}>
        <p className="text-sm text-destructive">{error}</p>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Tentar gerar novamente
          </Button>
        )}
      </div>
    );
  }

  if (!svg) {
    return (
      <div className={`rounded-lg border bg-muted/30 p-8 text-center text-muted-foreground text-sm ${className}`}>
        Carregando diagrama...
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={handleDownloadPng} className="gap-1.5">
            <Download className="h-4 w-4" />
            Baixar Diagrama (PNG)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setScale((s) => Math.min(1.5, s + 0.15))}
            disabled={scale >= 1.5}
            title="Aumentar zoom"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setScale((s) => Math.max(0.5, s - 0.15))}
            disabled={scale <= 0.5}
            title="Diminuir zoom"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setScale(1)}
            title="Resetar zoom"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="overflow-auto rounded-lg border p-4 min-h-[200px] flex items-center justify-center"
        style={{ backgroundColor: palette.themeVariables.background, borderColor: `${palette.themeVariables.lineColor}40` }}
      >
        <div
          className="mermaid-svg-container [&_svg]:max-w-full [&_svg]:h-auto transition-transform duration-200"
          style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>
  );
}

export default MermaidRenderer;
